import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { LoginService } from 'src/app/authentication/login/login.service';
import { SnackBarService } from 'src/app/services/snack-bar.service';
import { EmitirDeclaracoesPadService } from './emitir-declaracao-pad.service';
import {
  CertidaoPadDialogComponent,
  CertidaoPadDialogData
} from './certidao-pad-dialog/certidao-pad-dialog.component';
import { CertidaoPreviewDialogComponent } from './certidao-preview-dialog/certidao-preview-dialog.component';

const MENSAGEM_PENDENCIAS =
  'Servidor possui pendências, solicitar a Declaração no e-mail corregedoriageral@goias.gov.br';

@Component({
  selector: 'app-emitir-declaracao-pad',
  templateUrl: './emitir-declaracao-pad.component.html',
  styleUrls: ['./emitir-declaracao-pad.component.css']
})
export class EmitirDeclaracaoPadComponent implements OnInit {
  servidorForm: UntypedFormGroup;
  emitirDisabled = true;
  consultaLoading = false;
  downloadLoading = false;

  constructor(
    private fb: UntypedFormBuilder,
    private emitirDeclaracoesPadService: EmitirDeclaracoesPadService,
    private snackBar: SnackBarService,
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private loginService: LoginService
  ) {
    this.servidorForm = this.fb.group({
      id: [],
      nome: [{ value: null, disabled: true }, Validators.required],
      cpf: [{ value: null, disabled: true }]
    });
  }

  ngOnInit(): void {}

  buscarServidor(servidor: any): void {
    this.servidorForm.patchValue(servidor);
    this.emitirDisabled = !this.servidorForm.getRawValue().id;
  }

  consultar(): void {
    const raw = this.servidorForm.getRawValue();
    const cpf = raw.cpf;
    if (!cpf) {
      this.snackBar.showSnackBarError('Selecione um servidor antes de consultar.');
      return;
    }
    this.consultaLoading = true;
    this.emitirDeclaracoesPadService.compararConsultas({ cpf, servidorId: raw.id }).subscribe({
      next: (res: any) => {
        this.consultaLoading = false;
        const resultado = res?.resultado || {};
        const consultaLegado = resultado?.consulta_legado || {};
        const consultaAtual = resultado?.consulta_atual || {};
        const possuiPendenciasLegado = Number(consultaLegado?.quantidade_processos || 0) > 0;
        const possuiPendenciasAtual = !!consultaAtual?.possui_pendencias;
        const possuiPendencias = resultado?.possui_pendencias ?? (possuiPendenciasLegado || possuiPendenciasAtual);
        const emissaoCompleta = resultado?.emissaoCompleta ?? this.loginService.isDeclaracaoEmissaoCompleta();
        const dataConsulta = new Date();
        const dialogData: CertidaoPadDialogData = {
          dataConsulta,
          cpf: this.formatarCpf(String(cpf || '')),
          nome: raw.nome || '',
          situacao: possuiPendencias ? 'possui_pendencias' : 'nada_consta',
          servidorId: raw.id,
          podeEmitir: true,
          mensagemPendencias: possuiPendencias ? MENSAGEM_PENDENCIAS : undefined,
          emissaoCompleta: !!emissaoCompleta,
          processosSispac: consultaAtual?.processos || [],
          processosLegado: consultaLegado?.processos || []
        };
        const dialogRef = this.dialog.open(CertidaoPadDialogComponent, {
          width: '680px',
          maxWidth: '95vw',
          data: dialogData,
          disableClose: false
        });
        dialogRef.afterClosed().subscribe((result?: {
          emitir: boolean;
          forcarNadaConsta?: boolean;
          observacoes?: string;
          processosSelecionados?: string[];
        }) => {
          if (result?.emitir && raw.id) {
            this.abrirPreviewEDownload(raw.id, result.forcarNadaConsta, result.observacoes, result.processosSelecionados);
          }
        });
      },
      error: (err) => {
        this.consultaLoading = false;
        this.snackBar.showSnackBarError(
          this.extrairMensagemErro(err, 'Falha ao consultar as bases (SISPAC e legado).')
        );
      }
    });
  }

  private formatarCpf(cpf: string): string {
    const n = cpf.replace(/\D/g, '');
    if (n.length !== 11) return cpf;
    return n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }

  private montarParams(
    servidorId: number,
    forcarNadaConsta?: boolean,
    observacoes?: string,
    processosSelecionados?: string[]
  ): { servidorId: number; forcarNadaConsta?: boolean; observacoes?: string; cpfConsulta?: string; processosSelecionados?: string } {
    const raw = this.servidorForm.getRawValue();
    const params: ReturnType<typeof this.montarParams> = { servidorId };
    if (forcarNadaConsta) params.forcarNadaConsta = true;
    if (observacoes) params.observacoes = observacoes;
    if (raw?.cpf) params.cpfConsulta = String(raw.cpf).replace(/\D/g, '');
    if (processosSelecionados?.length) {
      params.processosSelecionados = processosSelecionados.join(',');
    }
    return params;
  }

  private abrirPreviewEDownload(
    servidorId: number,
    forcarNadaConsta?: boolean,
    observacoes?: string,
    processosSelecionados?: string[]
  ): void {
    const servidorIdNumerico = Number(servidorId);
    if (!Number.isFinite(servidorIdNumerico) || servidorIdNumerico <= 0) {
      this.snackBar.showSnackBarError('Servidor inválido para emissão da declaração.');
      return;
    }

    this.downloadLoading = true;
    const params = this.montarParams(servidorIdNumerico, forcarNadaConsta, observacoes, processosSelecionados);

    this.emitirDeclaracoesPadService.previewDeclaracao(params).subscribe({
      next: (htmlContent: any) => {
        this.downloadLoading = false;
        const previewRef = this.dialog.open(CertidaoPreviewDialogComponent, {
          width: '860px',
          maxWidth: '95vw',
          maxHeight: '95vh',
          data: { htmlContent: typeof htmlContent === 'string' ? htmlContent : String(htmlContent) }
        });
        previewRef.afterClosed().subscribe((confirmar: boolean) => {
          if (confirmar) {
            this.executarDownload(servidorIdNumerico, forcarNadaConsta, observacoes, processosSelecionados);
          }
        });
      },
      error: (error) => {
        console.error(error);
        this.downloadLoading = false;
        this.snackBar.showSnackBarError(
          this.extrairMensagemErro(error, 'Erro ao gerar pré-visualização da declaração.')
        );
      }
    });
  }

  private executarDownload(
    servidorId: number,
    forcarNadaConsta?: boolean,
    observacoes?: string,
    processosSelecionados?: string[]
  ): void {
    this.downloadLoading = true;
    const params = this.montarParams(servidorId, forcarNadaConsta, observacoes, processosSelecionados);

    this.emitirDeclaracoesPadService.downloadDeclaracao(params).subscribe({
      next: (declaracao: any) => {
        this.downloadLoading = false;
        const binaryData = atob(declaracao.pdf);
        const len = binaryData.length;
        const buffer = new ArrayBuffer(len);
        const view = new Uint8Array(buffer);
        for (let i = 0; i < len; i++) {
          view[i] = binaryData.charCodeAt(i);
        }
        const pdfBlob = new Blob([view], { type: 'application/pdf' });
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = declaracao.fileName;
        link.click();
        setTimeout(() => {
          URL.revokeObjectURL(url);
          link.remove();
        }, 100);
      },
      error: (error) => {
        console.error(error);
        this.downloadLoading = false;
        const errors = error?.error?.errors;
        const firstValidationError = errors ? Object.values(errors)[0] : null;
        const firstValidationMessage = Array.isArray(firstValidationError) ? firstValidationError[0] : null;
        this.snackBar.showSnackBarError(
          firstValidationMessage || this.extrairMensagemErro(error, 'Erro ao gerar declaração.')
        );
      }
    });
  }

  private extrairMensagemErro(err: any, fallback: string): string {
    return err?.error?.erro || err?.error?.message || err?.message || fallback;
  }
}
